import React from "react";
import { Box, Chip } from "@mui/material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import ChatIcon from "@mui/icons-material/Chat";
import MicIcon from "@mui/icons-material/Mic";
import { getChannelTypeById } from "../helpers/constants";

/** Email provider identifier (from API or channel config) */
export type EmailProviderType = "gmail" | "office365" | string;

/** Messaging provider identifier (from API) */
export type MessagingProviderType = "regular" | "whatsapp" | "slack" | "google_chat" | "google_chat_inbox" | "teams" | string;

export interface ChannelTypeChipProps {
  /** Channel type: email, message, or voice */
  channelType: "email" | "message" | "voice";
  /** For email channels: show Gmail/Outlook icon when set (e.g. 'gmail', 'office365') */
  emailProvider?: EmailProviderType | null;
  /** For message channels: show provider icon when set (e.g. 'slack', 'whatsapp', 'teams', 'google_chat') */
  messagingProvider?: MessagingProviderType | null;
  /** Chip size */
  size?: "small" | "medium";
  /** Optional sx overrides */
  sx?: object;
}

/**
 * Renders a channel type chip with optional provider-specific icon (Gmail/Outlook for email,
 * Slack/WhatsApp/Teams/Google Chat for message). Used in channel tiles and thread page header.
 */
const ChannelTypeChip: React.FC<ChannelTypeChipProps> = ({
  channelType,
  emailProvider,
  messagingProvider,
  size = "small",
  sx = {},
}) => {
  const channelTypeConfig = getChannelTypeById(channelType);
  if (!channelTypeConfig) return null;

  const getIcon = (): React.ReactNode => {
    // Email: use Gmail or Outlook icon when provider is known
    if (channelType === "email") {
      const provider = (emailProvider || "").toLowerCase();
      if (provider === "gmail") {
        return (
          <Box
            component="img"
            src="/images/apps/gmail.png"
            alt="Gmail"
            sx={{
              width: 14,
              height: 14,
              minWidth: 14,
              minHeight: 14,
              maxWidth: 14,
              maxHeight: 14,
              objectFit: "contain",
              display: "block",
              flexShrink: 0,
            }}
          />
        );
      }
      if (provider === "office365" || provider === "outlook" || provider === "microsoft") {
        return (
          <Box
            component="img"
            src="/images/apps/outlook-v2.png"
            alt="Outlook"
            sx={{
              width: 14,
              height: 14,
              minWidth: 14,
              minHeight: 14,
              maxWidth: 14,
              maxHeight: 14,
              objectFit: "contain",
              display: "block",
              flexShrink: 0,
            }}
          />
        );
      }
      return <EmailOutlinedIcon sx={{ fontSize: 14 }} />;
    }

    // Message: use provider-specific icon when set
    if (channelType === "message") {
      const provider = (messagingProvider || "regular") as string;
      switch (provider) {
        case "whatsapp":
          return (
            <Box
              component="img"
              src="/images/apps/whatsapp.png"
              alt="WhatsApp"
              sx={{
                width: 14,
                height: 14,
                minWidth: 14,
                minHeight: 14,
                maxWidth: 14,
                maxHeight: 14,
                objectFit: "contain",
                display: "block",
                flexShrink: 0,
              }}
            />
          );
        case "slack":
          return (
            <Box
              component="img"
              src="/images/apps/slack.png"
              alt="Slack"
              sx={{
                width: 14,
                height: 14,
                minWidth: 14,
                minHeight: 14,
                maxWidth: 14,
                maxHeight: 14,
                objectFit: "contain",
                display: "block",
                flexShrink: 0,
              }}
            />
          );
        case "google_chat":
        case "google_chat_inbox":
          return (
            <Box
              component="img"
              src="/images/apps/gchat.png"
              alt={provider === "google_chat_inbox" ? "Google Chat Inbox" : "Google Chat"}
              sx={{
                width: 14,
                height: 14,
                minWidth: 14,
                minHeight: 14,
                maxWidth: 14,
                maxHeight: 14,
                objectFit: "contain",
                display: "block",
                flexShrink: 0,
              }}
            />
          );
        case "teams":
          return (
            <Box
              component="img"
              src="/images/apps/teams.png"
              alt="Microsoft Teams"
              sx={{
                width: 14,
                height: 14,
                minWidth: 14,
                minHeight: 14,
                maxWidth: 14,
                maxHeight: 14,
                objectFit: "contain",
                display: "block",
                flexShrink: 0,
              }}
            />
          );
        default:
          return <ChatIcon sx={{ fontSize: 14 }} />;
      }
    }

    // Voice: use default Mic icon
    if (channelType === "voice") {
      return <MicIcon sx={{ fontSize: 14 }} />;
    }

    return null;
  };

  const iconElement = getIcon();

  return (
    <Chip
      {...(iconElement ? { icon: iconElement as React.ReactElement } : {})}
      label={channelTypeConfig.label}
      size={size}
      sx={{
        height: 22,
        fontSize: "0.65rem",
        fontWeight: 500,
        backgroundColor: `${channelTypeConfig.color}15`,
        color: channelTypeConfig.color,
        border: `1px solid ${channelTypeConfig.color}40`,
        "& .MuiChip-icon": {
          color: channelTypeConfig.color,
        },
        ...sx,
      }}
    />
  );
};

export default ChannelTypeChip;
